import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getRules, createRule, updateRule, deleteRule } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus,
  Pencil,
  Trash,
  Funnel,
  Lightning,
  Clock,
  Buildings,
  Warning,
  Brain
} from '@phosphor-icons/react';

const ruleTypeIcons = {
  company: Buildings,
  industry: Funnel,
  policy: Lightning,
  time_based: Clock,
  exception: Warning
};

const ruleTypeColors = {
  company: 'bg-blue-50 border-blue-200 text-blue-700',
  industry: 'bg-violet-50 border-violet-200 text-violet-700',
  policy: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  time_based: 'bg-amber-50 border-amber-200 text-amber-700',
  exception: 'bg-red-50 border-red-200 text-red-700'
};

export default function RulesEnginePage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'policy',
    condition_vendor: '',
    condition_amount_min: '',
    condition_amount_max: '',
    condition_description: '',
    action_account: '',
    action_flag_review: false,
    action_block: false,
    action_note: '',
    priority: 10,
    is_active: true
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await getRules();
      setRules(response.data.rules);
    } catch (error) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rule_type: 'policy',
      condition_vendor: '',
      condition_amount_min: '',
      condition_amount_max: '',
      condition_description: '',
      action_account: '',
      action_flag_review: false,
      action_block: false,
      action_note: '',
      priority: 10,
      is_active: true
    });
    setEditingRule(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const ruleData = {
      name: formData.name,
      rule_type: formData.rule_type,
      condition: {
        ...(formData.condition_vendor && { vendor_name: formData.condition_vendor }),
        ...(formData.condition_amount_min && { amount_min: parseFloat(formData.condition_amount_min) }),
        ...(formData.condition_amount_max && { amount_max: parseFloat(formData.condition_amount_max) }),
        ...(formData.condition_description && { description_contains: formData.condition_description })
      },
      action: {
        ...(formData.action_account && { suggested_account: formData.action_account }),
        flag_review: formData.action_flag_review,
        block_auto_post: formData.action_block,
        ...(formData.action_note && { note: formData.action_note })
      },
      priority: parseInt(formData.priority),
      is_active: formData.is_active
    };

    try {
      if (editingRule) {
        await updateRule(editingRule.rule_id, ruleData);
        toast.success('Rule updated');
      } else {
        await createRule(ruleData);
        toast.success('Rule created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchRules();
    } catch (error) {
      toast.error('Failed to save rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      condition_vendor: rule.condition?.vendor_name || '',
      condition_amount_min: rule.condition?.amount_min || '',
      condition_amount_max: rule.condition?.amount_max || '',
      condition_description: rule.condition?.description_contains || '',
      action_account: rule.action?.suggested_account || '',
      action_flag_review: rule.action?.flag_review || false,
      action_block: rule.action?.block_auto_post || false,
      action_note: rule.action?.note || '',
      priority: rule.priority,
      is_active: rule.is_active
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await deleteRule(ruleId);
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await updateRule(rule.rule_id, { ...rule, is_active: !rule.is_active });
      toast.success(`Rule ${rule.is_active ? 'disabled' : 'enabled'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  if (loading) {
    return (
      <Layout title="Rules Engine">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-zinc-950 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Rules Engine">
      <div data-testid="rules-engine-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-zinc-600">
              Business context rules override AI pattern recognition. Higher priority rules are evaluated first.
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="add-rule-button">
                <Plus size={18} className="mr-2" />
                Add Rule
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="rule-dialog">
              <DialogHeader>
                <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Rule Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Flag large AWS expenses"
                    required
                    className="rounded-sm"
                    data-testid="rule-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-500">Rule Type</Label>
                  <Select value={formData.rule_type} onValueChange={(v) => setFormData({ ...formData, rule_type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="rule-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Company-specific</SelectItem>
                      <SelectItem value="industry">Industry</SelectItem>
                      <SelectItem value="policy">Accounting Policy</SelectItem>
                      <SelectItem value="time_based">Time-based</SelectItem>
                      <SelectItem value="exception">Exception</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Conditions */}
                <div className="space-y-4">
                  <p className="text-xs uppercase tracking-widest text-zinc-500">Conditions (If...)</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-500">Vendor Contains</Label>
                      <Input
                        value={formData.condition_vendor}
                        onChange={(e) => setFormData({ ...formData, condition_vendor: e.target.value })}
                        placeholder="e.g., AWS, Payroll"
                        className="rounded-sm"
                        data-testid="condition-vendor-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-500">Description Contains</Label>
                      <Input
                        value={formData.condition_description}
                        onChange={(e) => setFormData({ ...formData, condition_description: e.target.value })}
                        placeholder="e.g., hosting, tax"
                        className="rounded-sm"
                        data-testid="condition-description-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-500">Amount Min ($)</Label>
                      <Input
                        type="number"
                        value={formData.condition_amount_min}
                        onChange={(e) => setFormData({ ...formData, condition_amount_min: e.target.value })}
                        placeholder="0.00"
                        className="rounded-sm font-mono"
                        data-testid="condition-amount-min-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-zinc-500">Amount Max ($)</Label>
                      <Input
                        type="number"
                        value={formData.condition_amount_max}
                        onChange={(e) => setFormData({ ...formData, condition_amount_max: e.target.value })}
                        placeholder="99999.99"
                        className="rounded-sm font-mono"
                        data-testid="condition-amount-max-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-4">
                  <p className="text-xs uppercase tracking-widest text-zinc-500">Actions (Then...)</p>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Suggest Account</Label>
                    <Input
                      value={formData.action_account}
                      onChange={(e) => setFormData({ ...formData, action_account: e.target.value })}
                      placeholder="e.g., 6200 - Cloud Services"
                      className="rounded-sm"
                      data-testid="action-account-input"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <Label className="text-sm">Flag for human review</Label>
                    <Switch
                      checked={formData.action_flag_review}
                      onCheckedChange={(checked) => setFormData({ ...formData, action_flag_review: checked })}
                      data-testid="action-flag-review-switch"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <Label className="text-sm">Block auto-posting</Label>
                    <Switch
                      checked={formData.action_block}
                      onCheckedChange={(checked) => setFormData({ ...formData, action_block: checked })}
                      data-testid="action-block-switch"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-500">Note/Explanation</Label>
                    <Textarea
                      value={formData.action_note}
                      onChange={(e) => setFormData({ ...formData, action_note: e.target.value })}
                      placeholder="Why this rule exists..."
                      className="rounded-sm resize-none"
                      rows={2}
                      data-testid="action-note-input"
                    />
                  </div>
                </div>

                {/* Priority and Active */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-zinc-500">Priority</Label>
                    <Input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      min="0"
                      max="100"
                      className="rounded-sm font-mono"
                      data-testid="rule-priority-input"
                    />
                  </div>
                  <div className="flex items-center justify-between pt-6">
                    <Label className="text-sm">Active</Label>
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="rule-active-switch"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }} className="rounded-sm">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm" data-testid="save-rule-button">
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Rules List */}
        {rules.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
            <Funnel size={48} className="mx-auto mb-4 text-zinc-300" />
            <p className="font-medium mb-2">No context rules yet</p>
            <p className="text-sm text-zinc-500 mb-4">
              Create rules to define your business context and override AI pattern recognition
            </p>
            <Button onClick={() => setIsDialogOpen(true)} className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm">
              <Plus size={18} className="mr-2" />
              Create First Rule
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rules.map((rule) => {
              const Icon = ruleTypeIcons[rule.rule_type] || Funnel;
              const colorClass = ruleTypeColors[rule.rule_type] || ruleTypeColors.policy;
              
              return (
                <div
                  key={rule.rule_id}
                  data-testid={`rule-card-${rule.rule_id}`}
                  className={`bg-white border border-zinc-200 rounded-sm p-6 ${!rule.is_active ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-sm border ${colorClass}`}>
                        <Icon size={20} weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-medium">{rule.name}</h3>
                        <p className="text-xs uppercase tracking-widest text-zinc-400">
                          {rule.rule_type?.replace('_', ' ')} • Priority {rule.priority}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(rule)}
                        className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-sm transition-colors"
                        data-testid={`edit-rule-${rule.rule_id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(rule.rule_id)}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                        data-testid={`delete-rule-${rule.rule_id}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Conditions */}
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Conditions</p>
                    <div className="flex flex-wrap gap-2">
                      {rule.condition?.vendor_name && (
                        <span className="px-2 py-1 text-xs bg-zinc-100 rounded-sm">
                          Vendor: {rule.condition.vendor_name}
                        </span>
                      )}
                      {rule.condition?.amount_min && (
                        <span className="px-2 py-1 text-xs bg-zinc-100 rounded-sm font-mono">
                          Min: ${rule.condition.amount_min}
                        </span>
                      )}
                      {rule.condition?.amount_max && (
                        <span className="px-2 py-1 text-xs bg-zinc-100 rounded-sm font-mono">
                          Max: ${rule.condition.amount_max}
                        </span>
                      )}
                      {rule.condition?.description_contains && (
                        <span className="px-2 py-1 text-xs bg-zinc-100 rounded-sm">
                          Desc: {rule.condition.description_contains}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mb-4">
                    <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Actions</p>
                    <div className="flex flex-wrap gap-2">
                      {rule.action?.suggested_account && (
                        <span className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
                          → {rule.action.suggested_account}
                        </span>
                      )}
                      {rule.action?.flag_review && (
                        <span className="px-2 py-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-sm">
                          Flag Review
                        </span>
                      )}
                      {rule.action?.block_auto_post && (
                        <span className="px-2 py-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-sm">
                          Block Auto-Post
                        </span>
                      )}
                    </div>
                  </div>

                  {rule.action?.note && (
                    <p className="text-xs text-zinc-500 italic">{rule.action.note}</p>
                  )}

                  {rule.auto_generated && (
                    <div className="mt-4 flex items-center gap-2 text-xs text-violet-600">
                      <Brain size={14} />
                      <span>Auto-generated from correction</span>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between">
                    <span className="text-xs text-zinc-400">
                      {rule.is_active ? 'Active' : 'Disabled'}
                    </span>
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => handleToggleActive(rule)}
                      data-testid={`toggle-rule-${rule.rule_id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
